import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useExperience } from '../../contexts/ExperienceContext';
import { needById, needs } from '../../data/needs';
import { brand } from '../../data/palette';
import { NeedDetail } from '../needs/NeedDetail';
import { NeedsOrbit } from '../needs/NeedsOrbit';
import { NeedsRibbon } from '../needs/NeedsRibbon';
import { Advance } from '../ui/Advance';
import { Eyebrow, Stage, StageTitle } from '../ui/Stage';
import type { NeedId } from '../../types/filtek';
interface NeedsStageProps {
  layout: 'ribbon' | 'orbit';
}

/** The family introduced through clinical need — never as a wall of packaging. */
export function NeedsStage({
  layout
}: NeedsStageProps) {
  const {
    signals,
    push,
    next,
    direction,
    primaryNeed
  } = useExperience();
  const [selected, setSelected] = useState<NeedId | undefined>();
  const active = selected ? needById(selected) : undefined;
  const select = (id: NeedId) => {
    setSelected(id === selected ? undefined : id);
    push('exploredNeeds', id);
  };
  const exploredCount = signals.exploredNeeds.length;
  return <Stage direction={direction} align="start" className="min-h-screen py-28 sm:py-32" labelledBy="needs-title" data-id="element-277" data-mp="pf6vuif3z1xe">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-278" data-mp="kj82shiu4vqn">
        <div data-id="element-279" data-mp="fjvdzr8elelf">
          <Eyebrow accent={active?.accent ?? brand.green} data-id="element-280" data-mp="fkeynfpfuh35">One family · five demands</Eyebrow>
          <StageTitle id="needs-title" size="display-sm" className="mt-6" data-id="element-281" data-mp="1d7vqejr23x7d">
            When you need…
          </StageTitle>
        </div>
        <p className="max-w-xs text-sm font-light leading-relaxed text-mute" data-id="element-282" data-mp="13fhmplgf4gik">
          Choose a demand and the family shows you which solution answers it.
        </p>
      </div>

      <div className="mt-12 sm:mt-16" data-id="element-283" data-mp="1vwajdckxplat">
        {layout === 'orbit' ? <NeedsOrbit selected={selected} explored={signals.exploredNeeds} matched={primaryNeed} onSelect={select} data-id="element-284" data-mp="1vxlopgu13141" /> : <NeedsRibbon selected={selected} explored={signals.exploredNeeds} matched={primaryNeed} onSelect={select} data-id="element-285" data-mp="3ldxvm7nw9enj" />}
      </div>

      <div className="mt-12 sm:mt-14" data-id="element-286" data-mp="3bn39d3anef23">
        <AnimatePresence mode="wait" data-id="element-287" data-mp="3gmplf9eqpp9b">
          {active ? <NeedDetail key={active.id} need={active} data-id="element-288" data-mp="3gkv2u1qxy98e" /> : <motion.p key="placeholder" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="max-w-md text-base font-light leading-relaxed text-mute" data-id="element-289" data-mp="32lvc8ojvbk9l">
              Five kinds of case. Five sets of demands. The solution appears once you have named
              the demand — not before.
            </motion.p>}
        </AnimatePresence>
      </div>

      {exploredCount > 0 && <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }} className="mt-16 flex flex-wrap items-center justify-between gap-6 border-t border-enamel/10 pt-8" data-id="element-290" data-mp="vvfcf7vmwmja">
          <p className="label-mono text-[10px] text-mute" data-id="element-291" data-mp="2q4kouossmcfx">
            {exploredCount} of {needs.length} explored
          </p>
          <Advance label={exploredCount < 2 ? 'Or move on' : 'Continue'} onClick={next} accent={active?.accent ?? brand.green} data-id="element-292" data-mp="2lc151a3iil2o" />
        </motion.div>}
    </Stage>;
}